/**
 * Audit log for screen-security events.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ScreenSecurityAuditStore.swift` —
 * per-event records, file-protection equivalent (IndexedDB), and the same
 * allow-listed API surface.
 *
 * ALLOW-LIST API INVARIANT (CRITICAL):
 *   The public interface exposes ONLY:
 *     - `record`  — append an audit event
 *     - `recent`  — retrieve the N most-recent events
 *     - `count`   — total number of stored events
 *
 *   There is NO `upload`, NO `sync`, NO `sendToServer` method.
 *   This is a type-level AND runtime invariant. Audit data is local-only.
 *   Mirrors the iOS rule: "allow-list API surface; no upload/sync methods".
 *
 * iOS analogue: `ScreenSecurityAuditStore.swift`.
 *   - `record(_:)` → `record(event)`
 *   - `recentEvents(limit:)` → `recent(limit?)`
 *   - `totalEventCount()` → `count()`
 *
 * Storage: IndexedDB, database per instance (injectable name for test isolation).
 * Events are stored newest-first by timestamp. Older events beyond `MAX_EVENTS`
 * are pruned on each write (same FIFO eviction as iOS store).
 *
 * NO window / document references. Pure IndexedDB.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Categories of screen-security audit events. */
export type ScreenSecurityEventKind =
  | "captureDetected"
  | "blurApplied"
  | "blurLifted"
  | "peerNotified";

/** A single screen-security audit record. */
export interface ScreenSecurityAuditEvent {
  /** Monotonic event ID (UUIDv4 or timestamp-based; set by `record`). */
  readonly id: string;
  readonly kind: ScreenSecurityEventKind;
  /** Unix timestamp (ms). */
  readonly occurredAt: number;
  /** Conversation context (optional; may be absent for global events). */
  readonly conversationId?: string;
  /** Human-readable detail, for diagnostics only. */
  readonly detail?: string;
}

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

const IDB_DB_VERSION = 1;
const STORE_NAME = "screen_security_audit";
/** Maximum number of events retained. FIFO eviction beyond this cap. */
const MAX_EVENTS = 1_000;

// ---------------------------------------------------------------------------
// ScreenSecurityAuditStore
// ---------------------------------------------------------------------------

/**
 * Allow-listed API surface for the screen-security audit log.
 *
 * Only `record`, `recent`, and `count` are exposed. No upload, sync, or
 * server-communication methods exist — by design.
 */
export interface IScreenSecurityAuditStore {
  /**
   * Append a screen-security event to the audit log.
   * FIFO-evicts oldest events when the total exceeds MAX_EVENTS.
   */
  record(
    event: Omit<ScreenSecurityAuditEvent, "id" | "occurredAt">,
  ): Promise<void>;

  /**
   * Retrieve the most-recent `limit` events, newest first.
   * @param limit - Maximum number of events to return (default: 20).
   */
  recent(limit?: number): Promise<readonly ScreenSecurityAuditEvent[]>;

  /**
   * Total number of events currently stored.
   */
  count(): Promise<number>;
}

/**
 * IndexedDB-backed implementation of `IScreenSecurityAuditStore`.
 *
 * ALLOW-LIST: Only `record`, `recent`, `count` are public. No upload/sync.
 */
export class ScreenSecurityAuditStore implements IScreenSecurityAuditStore {
  private readonly dbName: string;
  private db: IDBDatabase | null = null;

  constructor(
    dbName = "kaether:screen-security-audit:v1",
  ) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // Public API — allow-list (record / recent / count ONLY)
  // -------------------------------------------------------------------------

  async record(
    event: Omit<ScreenSecurityAuditEvent, "id" | "occurredAt">,
  ): Promise<void> {
    const db = await this._open();
    const full: ScreenSecurityAuditEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      occurredAt: Date.now(),
      ...event,
    };

    await this._idbRW(db, (store) => {
      store.add(full);
    });

    await this._prune(db);
  }

  async recent(limit = 20): Promise<readonly ScreenSecurityAuditEvent[]> {
    const db = await this._open();
    return this._idbRO(db, (store) => {
      return new Promise<readonly ScreenSecurityAuditEvent[]>(
        (resolve, reject) => {
          const results: ScreenSecurityAuditEvent[] = [];
          // Use a descending cursor on the `occurredAt` index for newest-first order.
          const index = store.index("occurredAt");
          const request = index.openCursor(null, "prev");
          request.onsuccess = () => {
            const cursor = request.result;
            if (!cursor || results.length >= limit) {
              resolve(results);
              return;
            }
            results.push(cursor.value as ScreenSecurityAuditEvent);
            cursor.continue();
          };
          request.onerror = () => reject(request.error);
        },
      );
    });
  }

  async count(): Promise<number> {
    const db = await this._open();
    return this._idbRO(db, (store) => {
      return new Promise<number>((resolve, reject) => {
        const req = store.count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    });
  }

  // -------------------------------------------------------------------------
  // Private IDB helpers
  // -------------------------------------------------------------------------

  private _open(): Promise<IDBDatabase> {
    if (this.db) return Promise.resolve(this.db);
    return new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, IDB_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
          store.createIndex("occurredAt", "occurredAt", { unique: false });
        }
      };
      req.onsuccess = () => {
        this.db = req.result;
        resolve(this.db);
      };
      req.onerror = () => reject(req.error);
    });
  }

  private _idbRW(db: IDBDatabase, fn: (store: IDBObjectStore) => void): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      fn(store);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private _idbRO<T>(
    db: IDBDatabase,
    fn: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    return fn(store);
  }

  /** Prune oldest events beyond MAX_EVENTS. */
  private async _prune(db: IDBDatabase): Promise<void> {
    const currentCount = await this._idbRO(db, (store) => {
      return new Promise<number>((resolve, reject) => {
        const req = store.count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    });

    if (currentCount <= MAX_EVENTS) return;

    const excess = currentCount - MAX_EVENTS;
    await this._idbRW(db, (store) => {
      // Collect the oldest `excess` keys for deletion.
      const index = store.index("occurredAt");
      const req = index.openCursor(null, "next"); // ascending = oldest first
      let deleted = 0;
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor || deleted >= excess) return;
        cursor.delete();
        deleted++;
        cursor.continue();
      };
    });
  }
}
